/** Buch per Barcode oder von Hand aufnehmen.
 *
 *  Der Weg, wie Bücher NACH dem Import ins Regal kommen, ohne CSV: im Laden den
 *  Strichcode scannen, Metadaten kommen automatisch, das Buch landet auf „will
 *  ich lesen".
 *
 *  Der Scanner nutzt die BarcodeDetector-Schnittstelle des Browsers. Wo es sie
 *  nicht gibt (Safari, Firefox), wird das ehrlich gesagt und die ISBN von Hand
 *  eingegeben — kein halbgarer Ersatz mit Fremdbibliothek.
 */

import { useEffect, useRef, useState } from "react";
import type { Status } from "../../../shared/types";
import { STATUS_LABEL } from "../../../shared/types";
import * as api from "../lib/api";
import { useStore } from "../lib/store";

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
}
declare global {
  interface Window {
    BarcodeDetector?: new (opts?: {
      formats?: string[];
    }) => BarcodeDetectorLike;
  }
}

export function Erfassen() {
  const { owner, schreibe, neuLaden, melde } = useStore();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [scannt, setScannt] = useState(false);
  const [isbn, setIsbn] = useState("");
  const [treffer, setTreffer] = useState<api.LookupResult | null>(null);
  const [status, setStatus] = useState<Status>("want");
  const [laeuft, setLaeuft] = useState(false);
  const [titel, setTitel] = useState("");
  const [autor, setAutor] = useState("");

  const scannerMoeglich =
    typeof window !== "undefined" && !!window.BarcodeDetector;

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  async function scannerStarten() {
    if (!scannerMoeglich) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setScannt(true);

      // Nur echte Formate: "isbn" ist KEIN BarcodeFormat und lässt den
      // Konstruktor mit einem TypeError scheitern. ISBNs stecken als EAN-13
      // im Strichcode, ean_13 genügt also.
      const detector = new window.BarcodeDetector!({
        formats: ["ean_13", "ean_8"],
      });
      const suchen = async () => {
        if (!videoRef.current || !streamRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          const code = codes.find((c) => /^97[89]\d{10}$/.test(c.rawValue));
          if (code) {
            setIsbn(code.rawValue);
            scannerStoppen();
            void nachschlagen(code.rawValue);
            return;
          }
        } catch {
          /* einzelner Frame ohne Code — weitermachen */
        }
        if (streamRef.current) requestAnimationFrame(() => void suchen());
      };
      void suchen();
    } catch (err) {
      // Wichtig: Der Fehler kann NACH getUserMedia auftreten — dann läuft die
      // Kamera schon. Ohne dieses Stoppen bliebe sie an, ohne Knopf zum
      // Ausschalten (setScannt(true) ist in diesem Pfad nicht mehr erreichbar).
      scannerStoppen();
      melde(
        `Strichcode-Leser nicht verfügbar: ${err instanceof Error ? err.message : String(err)}. Die ISBN lässt sich unten eintippen.`,
        "fehler",
      );
    }
  }

  function scannerStoppen() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setScannt(false);
  }

  async function nachschlagen(wert?: string) {
    const code = (wert ?? isbn).trim();
    setLaeuft(true);
    try {
      const res = await api.lookup(
        code ? { isbn: code } : { title: titel, author: autor },
      );
      setTreffer(res.data);
      if (!res.data) {
        melde(
          "Nichts gefunden — Titel und Autor von Hand eintragen.",
          "warnung",
        );
      } else {
        setTitel(res.data.title);
        setAutor(res.data.author);
      }
    } catch (err) {
      melde(err instanceof Error ? err.message : String(err), "fehler");
    } finally {
      setLaeuft(false);
    }
  }

  async function anlegen() {
    if (!titel.trim()) {
      melde("Ohne Titel geht es nicht.", "fehler");
      return;
    }
    const neu = await schreibe(
      () =>
        api.addBook(owner, {
          title: titel.trim(),
          author: autor.trim(),
          isbn: isbn.trim() || treffer?.isbn || null,
          status,
          pages: treffer?.pages ?? null,
          pub_year: treffer?.pub_year ?? null,
          publisher: treffer?.publisher ?? null,
          description: treffer?.description ?? null,
        }),
      "Ins Regal gestellt.",
    );
    if (neu) {
      setIsbn("");
      setTitel("");
      setAutor("");
      setTreffer(null);
      await neuLaden();
    }
  }

  return (
    <>
      <section style={{ maxWidth: "46ch" }}>
        <h2 className="titelzeile">Buch aufnehmen</h2>
        <p className="lauftext">
          Im Laden den Strichcode scannen oder die ISBN eintippen — die Angaben
          kommen automatisch. Ohne ISBN geht es auch: Titel und Autor genügen.
        </p>
      </section>

      <div
        className="karte"
        style={{ marginTop: 26, display: "grid", gap: 16 }}
      >
        <div>
          <span className="feldname">Strichcode</span>
          {scannerMoeglich ? (
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {!scannt ? (
                <button
                  className="knopf stark"
                  onClick={() => void scannerStarten()}
                >
                  Kamera einschalten
                </button>
              ) : (
                <button className="knopf" onClick={scannerStoppen}>
                  Kamera aus
                </button>
              )}
              <video
                ref={videoRef}
                playsInline
                muted
                style={{
                  width: scannt ? "min(420px, 90vw)" : 0,
                  height: scannt ? "auto" : 0,
                  borderRadius: 2,
                  border: scannt ? "1px solid var(--line)" : "none",
                }}
              />
            </div>
          ) : (
            <p className="lauftext" style={{ fontSize: 14, margin: 0 }}>
              Dieser Browser kann keine Strichcodes lesen (nur Chrome und Edge
              können das). Die ISBN steht unter dem Code — eintippen geht
              genauso.
            </p>
          )}
        </div>

        <div>
          <span className="feldname">ISBN</span>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              style={{ width: "min(260px, 70vw)" }}
              inputMode="numeric"
              placeholder="978…"
              value={isbn}
              onChange={(e) => setIsbn(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void nachschlagen()}
            />
            <button
              className="knopf"
              disabled={laeuft}
              onClick={() => void nachschlagen()}
            >
              {laeuft ? "sucht …" : "Nachschlagen"}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: "2 1 260px" }}>
            <span className="feldname">Titel</span>
            <input
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              placeholder="Pflichtfeld"
            />
          </div>
          <div style={{ flex: "1 1 200px" }}>
            <span className="feldname">Autor</span>
            <input value={autor} onChange={(e) => setAutor(e.target.value)} />
          </div>
        </div>

        {treffer && (
          <p className="lauftext" style={{ fontSize: 14, margin: 0 }}>
            Gefunden über {treffer.source}
            {treffer.pub_year ? ` · ${treffer.pub_year}` : ""}
            {treffer.pages ? ` · ${treffer.pages} Seiten` : ""}
            {treffer.tags.length ? ` · ${treffer.tags.join(", ")}` : ""}
          </p>
        )}

        <div>
          <span className="feldname">Regalfach</span>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {(["want", "reading", "read", "dnf"] as Status[]).map((s) => (
              <button
                key={s}
                className={`chip${status === s ? " an" : ""}`}
                onClick={() => setStatus(s)}
              >
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <button
            className="knopf stark"
            disabled={!titel.trim()}
            onClick={() => void anlegen()}
          >
            Ins Regal stellen
          </button>
        </div>
      </div>
    </>
  );
}
